import { FC, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { CargoType, Permission, showError, WithChildren } from '../../_metronic/helpers'
import EmpresasClientePage from '../modules/apps/empresa/EmpresaPageCliente'
import TopBarProgress from 'react-topbar-progress-indicator'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { useAuth } from '../modules/auth'
import { ErroInesperado } from '../modules/errors/components/ErroInesperado'
import { appRoutes } from './routesConfig'
import { calcularRotaPermissoes } from './rotaPermissoes'
import { ChamadaAtivaProvider } from '../modules/apps/atendimento_medicos/chamada-ativa/ChamadaAtivaProvider'
import { SessaoAtendimentoProvider } from '../modules/apps/atendimento_medicos/chamada-ativa/SessaoAtendimentoProvider'
import { NotaVersaoDisparador } from '../modules/apps/notas-versao/components/NotaVersaoDisparador'

export const PrivateRoutes: FC = () => {
  const [permissions, setPermissions] = useState<Permission[] | null>(null)

  const { currentUser } = useAuth()

  useEffect(() => {
    try {
      setPermissions(calcularRotaPermissoes(currentUser))
    } catch (error) {
      showError(error)
      setPermissions([])
    }
  }, [currentUser])

  if (permissions === null) {
    return <TopBarProgress />
  }

  const isColaborador =
    permissions.includes(Permission.Usuario) &&
    currentUser?.vinculos?.some((v) => v.cargo === CargoType.Colaborador)

  // A chamada da teleconsulta precisa sobreviver à navegação (LO-829): providers ACIMA do SuspensedView.
  return (
    <ChamadaAtivaProvider>
      <SessaoAtendimentoProvider>
        {/* Dentro do provider da chamada: a nota não abre por cima de uma teleconsulta em curso. */}
        <NotaVersaoDisparador />
        <SuspensedView>
          <Routes>
            {appRoutes.map(({ path, element, allowedPermissions }) => {
              const canAccess =
                !allowedPermissions || allowedPermissions.some((p) => permissions.includes(p))
              return (
                <Route
                  key={path}
                  path={path}
                  element={canAccess ? element : <Navigate to='/error/404' />}
                />
              )
            })}
            {isColaborador && (
              <Route path='apps/empresasCliente/*' element={<EmpresasClientePage />} />
            )}
            <Route
              path='*'
              element={
                <Navigate
                  to={
                    permissions.includes(Permission.SuperGerente)
                      ? '/apps/relatorios'
                      : permissions.includes(Permission.Solicitante)
                      ? '/apps/pareceres'
                      : permissions.length === 1 && permissions.includes(Permission.Usuario)
                      ? '/apps/atendimentos/paciente'
                      : '/apps/atendimentosMedicos/lista'
                  }
                />
              }
            />
          </Routes>
        </SuspensedView>
      </SessaoAtendimentoProvider>
    </ChamadaAtivaProvider>
  )
}

/**
 * Suspense das páginas lazy mais o ErrorBoundary da área de conteúdo: um crash numa tela mostra a
 * tela de erro dentro do layout (menu e cabeçalho continuam), e o `key` pela rota faz o boundary
 * remontar quando o usuário navega pelo menu, em vez de ficar preso no erro.
 */
const SuspensedView: FC<WithChildren> = ({ children }) => {
  const { pathname } = useLocation()
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return (
    <Sentry.ErrorBoundary
      key={pathname}
      fallback={({ error, eventId, resetError }) => (
        <ErroInesperado
          variante='conteudo'
          error={error}
          eventId={eventId}
          onTentarNovamente={resetError}
        />
      )}
    >
      <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
    </Sentry.ErrorBoundary>
  )
}
